import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Paperclip, Trash2, Upload } from 'lucide-react';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  createEpisodeAuthorization,
  deleteEpisodeAuthorization,
  getPatientDocumentUrl,
  updateEpisodeAuthorization,
  uploadPatientDocument,
} from '../../services/api';
import type {
  AuthorizationCreate,
  AuthorizationResponse,
  AuthorizationUpdate,
  AuthorizationStatus,
  VisitResponse,
} from '../../services/api';
import type { PatientInsuranceResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { scrollToField } from '../../utils/scrollToField';
import { AlertTriangle } from 'lucide-react';
import { AUTHORIZATION_STATUS_OPTIONS } from './authorizationConstants';

interface AuthorizationDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  episodeId: string;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  patientInsurances: PatientInsuranceResponse[];
  defaultInsuranceId?: string | null;
  editingAuthorization?: AuthorizationResponse | null;
  /** Episode visits, used to warn when an edit would break linked-visit checks. */
  episodeVisits?: VisitResponse[];
  onSaved: () => void;
}

// Maps a visit discipline to its authorized-visits field key for warnings.
const DISCIPLINE_TO_VISIT_FIELD: Record<string, VisitCountField['key']> = {
  SN: 'sn_visits',
  PT: 'pt_visits',
  OT: 'ot_visits',
  ST: 'st_visits',
  MSW: 'msw_visits',
  HHA: 'hha_visits',
};

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required }) => (
  <p className='text-sm font-medium mb-1.5'>
    {children}
    {required && <span className='text-destructive ml-0.5'>*</span>}
  </p>
);

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const INPUT_ERROR_CLASS = INPUT_CLASS.replace(
  'border-input',
  'border-destructive'
);

interface VisitCountField {
  key:
    | 'sn_visits'
    | 'pt_visits'
    | 'ot_visits'
    | 'st_visits'
    | 'msw_visits'
    | 'hha_visits'
    | 'dietician_visits'
    | 'misc_visits';
  label: string;
}

const VISIT_COUNT_FIELDS: VisitCountField[] = [
  { key: 'sn_visits', label: 'SN Visits' },
  { key: 'pt_visits', label: 'PT Visits' },
  { key: 'ot_visits', label: 'OT Visits' },
  { key: 'st_visits', label: 'ST Visits' },
  { key: 'msw_visits', label: 'MSW Visits' },
  { key: 'hha_visits', label: 'HHA Visits' },
  { key: 'dietician_visits', label: 'Dietician Visits' },
  { key: 'misc_visits', label: 'Misc Visits' },
];

type VisitCounts = Record<VisitCountField['key'], string>;

const DEFAULT_VISIT_COUNTS: VisitCounts = {
  sn_visits: '0',
  pt_visits: '0',
  ot_visits: '0',
  st_visits: '0',
  msw_visits: '0',
  hha_visits: '0',
  dietician_visits: '0',
  misc_visits: '0',
};

const parseCount = (value: string): number | null => {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

// A non-empty value that fails to parse is a hard error; empty means "unset",
// which is allowed. The old behavior silently coerced invalid input to null,
// dropping the user's intent without telling them — mirror the backend's
// Field(ge=0) constraints by surfacing the error and blocking save instead.
const isInvalidCount = (value: string): boolean =>
  value.trim() !== '' && parseCount(value) === null;

export const AuthorizationDialog: React.FC<AuthorizationDialogProps> = ({
  open,
  onClose,
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  patientInsurances,
  defaultInsuranceId,
  editingAuthorization,
  episodeVisits = [],
  onSaved,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const isEditing = Boolean(editingAuthorization);

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [status, setStatus] = useState<AuthorizationStatus>('NOT_STARTED');
  const [patientInsuranceId, setPatientInsuranceId] = useState('');
  const [authNumber, setAuthNumber] = useState('');
  const [visitCounts, setVisitCounts] =
    useState<VisitCounts>(DEFAULT_VISIT_COUNTS);
  const [comment, setComment] = useState('');

  const [pendingFiles, setPendingFiles] = useState<
    Array<{ file: File; documentId?: string }>
  >([]);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const insuranceOptions = useMemo(
    () =>
      patientInsurances.map(ins => ({
        value: ins.id,
        label: ins.insurance_name,
      })),
    [patientInsurances]
  );

  const episodeStart = useMemo(
    () => parseISOToLocalDate(episodeStartDate ?? undefined) ?? undefined,
    [episodeStartDate]
  );
  const episodeEnd = useMemo(
    () => parseISOToLocalDate(episodeEndDate ?? undefined) ?? undefined,
    [episodeEndDate]
  );

  const resetForm = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStatus('NOT_STARTED');
    setPatientInsuranceId(defaultInsuranceId ?? '');
    setAuthNumber('');
    setVisitCounts(DEFAULT_VISIT_COUNTS);
    setComment('');
    setPendingFiles([]);
    setRemovedDocumentIds([]);
  };

  // Initialize form on open (prefill when editing, defaults when creating)
  useEffect(() => {
    if (!open) return;
    if (!editingAuthorization) {
      resetForm();
      // Prefill new authorizations with the episode's date range.
      setStartDate(episodeStart);
      setEndDate(episodeEnd);
      return;
    }
    const a = editingAuthorization;
    setStartDate(parseISOToLocalDate(a.start_date) ?? undefined);
    setEndDate(parseISOToLocalDate(a.end_date) ?? undefined);
    setStatus(a.status ?? 'NOT_STARTED');
    setPatientInsuranceId(a.patient_insurance_id ?? '');
    setAuthNumber(a.authorization_number ?? '');
    setVisitCounts({
      sn_visits: a.sn_visits?.toString() ?? '',
      pt_visits: a.pt_visits?.toString() ?? '',
      ot_visits: a.ot_visits?.toString() ?? '',
      st_visits: a.st_visits?.toString() ?? '',
      msw_visits: a.msw_visits?.toString() ?? '',
      hha_visits: a.hha_visits?.toString() ?? '',
      dietician_visits: a.dietician_visits?.toString() ?? '',
      misc_visits: a.misc_visits?.toString() ?? '',
    });
    setComment(a.comment ?? '');
    setPendingFiles([]);
    setRemovedDocumentIds([]);
  }, [open, editingAuthorization, episodeStart, episodeEnd]);

  const startDateOutOfEpisodeRange = Boolean(
    startDate &&
    ((episodeStart && startDate < episodeStart) ||
      (episodeEnd && startDate > episodeEnd))
  );
  const endDateOutOfEpisodeRange = Boolean(
    endDate &&
    ((episodeStart && endDate < episodeStart) ||
      (episodeEnd && endDate > episodeEnd))
  );

  // Per-field hard errors: a non-empty visit count that isn't a whole number >= 0.
  // These block save (below), matching the backend's Field(ge=0) constraints. Note
  // this is deliberately distinct from the non-blocking `validationWarnings`
  // (used > authorized), which stay soft.
  const fieldErrors = useMemo(() => {
    const errs: Partial<Record<VisitCountField['key'], string>> = {};
    for (const field of VISIT_COUNT_FIELDS) {
      if (isInvalidCount(visitCounts[field.key])) {
        errs[field.key] = 'Enter a whole number of visits (0 or more).';
      }
    }
    return errs;
  }, [visitCounts]);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  const isValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (endDate < startDate) return false;
    if (startDateOutOfEpisodeRange || endDateOutOfEpisodeRange) return false;
    if (hasFieldErrors) return false;
    return true;
  }, [
    startDate,
    endDate,
    startDateOutOfEpisodeRange,
    endDateOutOfEpisodeRange,
    hasFieldErrors,
  ]);

  // Visits currently linked to the auth being edited (for soft warnings).
  const linkedVisits = useMemo(
    () =>
      editingAuthorization
        ? episodeVisits.filter(
            v => v.authorization_id === editingAuthorization.id
          )
        : [],
    [editingAuthorization, episodeVisits]
  );

  // Non-blocking warnings: saving is still allowed, but these edits would make
  // the episode's authorization checks fail. Each links back to its field.
  const validationWarnings = useMemo(() => {
    const out: { message: string; anchor: string }[] = [];
    if (startDate && endDate) {
      for (const v of linkedVisits) {
        const d = parseISOToLocalDate(v.visit_date);
        if (d && (d < startDate || d > endDate)) {
          out.push({
            message: `${v.discipline ?? 'Visit'} visit on ${formatDateToMMDDYYYY(
              v.visit_date
            )} falls outside these authorization dates`,
            anchor: 'authdlg-dates',
          });
        }
      }
    }
    const linkedByDiscipline: Record<string, number> = {};
    for (const v of linkedVisits) {
      if (v.discipline) {
        linkedByDiscipline[v.discipline] =
          (linkedByDiscipline[v.discipline] ?? 0) + 1;
      }
    }
    for (const [discipline, count] of Object.entries(linkedByDiscipline)) {
      const fieldKey = DISCIPLINE_TO_VISIT_FIELD[discipline];
      if (!fieldKey) continue;
      const allowed = parseCount(visitCounts[fieldKey]) ?? 0;
      if (count > allowed) {
        out.push({
          message: `${count} ${discipline} visit${count === 1 ? '' : 's'} linked, but only ${allowed} authorized`,
          anchor: `authdlg-${fieldKey}`,
        });
      }
    }
    return out;
  }, [linkedVisits, startDate, endDate, visitCounts]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleViewDocument = async (documentId: string) => {
    try {
      const res = await getPatientDocumentUrl({
        path: {
          patient_id: patientId,
          document_id: documentId,
        },
      });
      if (res.error) {
        console.error('Failed to get document URL:', res.error);
        showError('Error', 'Unable to open attachment.');
        return;
      }
      const url = (res.data as { url?: string } | undefined)?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to get document URL:', err);
      showError('Error', 'Unable to open attachment.');
    }
  };

  const handleSave = async () => {
    if (!isValid || !startDate || !endDate) return;
    setSaving(true);
    try {
      // Keep successful uploads in state. Retrying after a later upload or
      // save failure reuses those documents instead of uploading duplicates.
      const documentIds: string[] = [];
      for (let index = 0; index < pendingFiles.length; index++) {
        const pending = pendingFiles[index];
        if (pending.documentId) {
          documentIds.push(pending.documentId);
          continue;
        }
        const uploadRes = await uploadPatientDocument({
          path: { patient_id: patientId },
          query: { display_name: pending.file.name },
          body: { file: pending.file },
        });
        if (uploadRes.error || !uploadRes.data) {
          showError(
            'Upload Failed',
            `Could not upload ${pending.file.name}. Other uploaded files are retained for retry.`
          );
          return;
        }
        const documentId = uploadRes.data.id;
        documentIds.push(documentId);
        setPendingFiles(current =>
          current.map((item, i) =>
            i === index ? { ...item, documentId } : item
          )
        );
      }

      const body: AuthorizationCreate & AuthorizationUpdate = {
        start_date: formatDateToISO(startDate),
        end_date: formatDateToISO(endDate),
        status,
        patient_insurance_id: patientInsuranceId || null,
        authorization_number: authNumber.trim() || null,
        sn_visits: parseCount(visitCounts.sn_visits),
        pt_visits: parseCount(visitCounts.pt_visits),
        ot_visits: parseCount(visitCounts.ot_visits),
        st_visits: parseCount(visitCounts.st_visits),
        msw_visits: parseCount(visitCounts.msw_visits),
        hha_visits: parseCount(visitCounts.hha_visits),
        dietician_visits: parseCount(visitCounts.dietician_visits),
        misc_visits: parseCount(visitCounts.misc_visits),
        comment: comment.trim() || null,
        ...(isEditing
          ? {
              add_document_ids: documentIds,
              remove_document_ids: removedDocumentIds,
            }
          : { document_ids: documentIds }),
      };

      let res;
      if (isEditing && editingAuthorization) {
        res = await updateEpisodeAuthorization({
          path: {
            episode_id: episodeId,
            authorization_id: editingAuthorization.id,
          },
          body,
        });
      } else {
        res = await createEpisodeAuthorization({
          path: { episode_id: episodeId },
          body,
        });
      }
      if (res.error) {
        console.error('Failed to save authorization:', res.error);
        showError('Save Failed', 'Failed to save authorization.');
        return;
      }
      showSuccess(
        'Authorization Saved',
        isEditing ? 'Authorization updated.' : 'Authorization created.'
      );
      handleClose();
      onSaved();
    } catch (err) {
      console.error('Error saving authorization:', err);
      showError('Save Failed', 'Failed to save authorization.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingAuthorization) return;
    setDeleting(true);
    try {
      const res = await deleteEpisodeAuthorization({
        path: {
          episode_id: episodeId,
          authorization_id: editingAuthorization.id,
        },
      });
      if (res.error) {
        console.error('Failed to delete authorization:', res.error);
        showError('Error', 'Failed to delete authorization.');
        return;
      }
      showSuccess('Deleted', 'Authorization deleted.');
      handleClose();
      onSaved();
    } catch (err) {
      console.error('Failed to delete authorization:', err);
      showError('Error', 'Failed to delete authorization.');
    } finally {
      setDeleting(false);
    }
  };

  const existingDocuments =
    editingAuthorization?.documents ??
    (editingAuthorization?.document_id
      ? [
          {
            id: editingAuthorization.document_id,
            file_name: editingAuthorization.document_file_name ?? 'Attachment',
            imported_at: null,
          },
        ]
      : []);

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o && !saving && !deleting) handleClose();
      }}
    >
      <DialogContent className='flex w-[calc(100vw-2rem)] max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>
            {isEditing ? 'Edit Authorization' : 'Add Authorization'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Record an insurance authorization for this episode.
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-5 overflow-y-auto'>
          {/* Non-blocking validation warnings (saving still allowed) */}
          {validationWarnings.length > 0 && (
            <div className='rounded-md border border-destructive/40 bg-destructive/5 p-3'>
              <div className='flex items-center gap-1.5 text-sm font-medium text-destructive'>
                <AlertTriangle className='h-4 w-4 shrink-0' />
                This authorization won&apos;t cover all of its linked visits
              </div>
              <ul className='mt-1.5 space-y-1'>
                {validationWarnings.map((w, i) => (
                  <li key={`${w.anchor}-${i}`}>
                    <button
                      type='button'
                      onClick={() => scrollToField(w.anchor, { focus: false })}
                      className='text-left text-xs text-destructive underline-offset-2 hover:underline'
                    >
                      {w.message}
                    </button>
                  </li>
                ))}
              </ul>
              <p className='mt-1.5 text-xs text-muted-foreground'>
                You can still save — these visits will show as authorization
                errors until resolved.
              </p>
            </div>
          )}

          {/* Dates */}
          <div
            data-field-anchor='authdlg-dates'
            className='grid grid-cols-1 sm:grid-cols-2 gap-4'
          >
            <div>
              <FieldLabel required>Start Date</FieldLabel>
              <DatePicker
                date={startDate}
                onDateChange={setStartDate}
                minDate={episodeStart}
                maxDate={episodeEnd}
              />
              {startDateOutOfEpisodeRange && (
                <p className='mt-1 text-xs text-destructive'>
                  Start date must fall within the episode dates.
                </p>
              )}
            </div>
            <div>
              <FieldLabel required>End Date</FieldLabel>
              <DatePicker
                date={endDate}
                onDateChange={setEndDate}
                minDate={episodeStart}
                maxDate={episodeEnd}
              />
              {startDate && endDate && endDate < startDate && (
                <p className='mt-1 text-xs text-destructive'>
                  End date must be on or after start date.
                </p>
              )}
              {endDateOutOfEpisodeRange && (
                <p className='mt-1 text-xs text-destructive'>
                  End date must fall within the episode dates.
                </p>
              )}
            </div>
          </div>

          {/* Status + Insurance */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <FieldLabel>Status</FieldLabel>
              <Select
                value={status}
                onValueChange={v => setStatus(v as AuthorizationStatus)}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select status…' />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORIZATION_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Insurance</FieldLabel>
              <Select
                value={patientInsuranceId}
                onValueChange={setPatientInsuranceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select insurance…' />
                </SelectTrigger>
                <SelectContent>
                  {insuranceOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Authorization Number */}
          <div>
            <FieldLabel>Authorization Number</FieldLabel>
            <input
              type='text'
              value={authNumber}
              onChange={e => setAuthNumber(e.target.value)}
              placeholder='Payer-issued authorization number'
              className={INPUT_CLASS}
            />
          </div>

          {/* Authorized Visits */}
          <div>
            <FieldLabel>Authorized Visits</FieldLabel>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
              {VISIT_COUNT_FIELDS.map(field => (
                <div key={field.key} data-field-anchor={`authdlg-${field.key}`}>
                  <p className='text-xs text-muted-foreground mb-1'>
                    {field.label}
                  </p>
                  <input
                    type='number'
                    min={0}
                    step={1}
                    value={visitCounts[field.key]}
                    onChange={e =>
                      setVisitCounts(prev => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    placeholder='—'
                    aria-invalid={Boolean(fieldErrors[field.key])}
                    className={
                      fieldErrors[field.key] ? INPUT_ERROR_CLASS : INPUT_CLASS
                    }
                  />
                  {fieldErrors[field.key] && (
                    <p className='mt-1 text-xs text-destructive'>
                      {fieldErrors[field.key]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <FieldLabel>Comment</FieldLabel>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder='Notes about this authorization…'
              rows={3}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          {/* Attachments */}
          <div>
            <FieldLabel>Attachments</FieldLabel>
            <p className='mb-2 text-xs text-muted-foreground'>
              Removing an attachment leaves the file in patient Documents.
            </p>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              className='hidden'
              aria-label='Add authorization attachments'
              disabled={saving}
              onChange={e => {
                const files = Array.from(e.target.files ?? []);
                setPendingFiles(current => [
                  ...current,
                  ...files.map(file => ({ file })),
                ]);
                e.target.value = '';
              }}
            />
            <ul className='space-y-2'>
              {existingDocuments.map(document => {
                const removed = removedDocumentIds.includes(document.id);
                return (
                  <li
                    key={document.id}
                    className='flex items-center gap-2 text-sm'
                  >
                    <Paperclip className='h-4 w-4 shrink-0 text-muted-foreground' />
                    <span
                      className={
                        removed
                          ? 'truncate line-through text-muted-foreground'
                          : 'truncate'
                      }
                    >
                      {document.file_name}
                    </span>
                    {document.imported_at && (
                      <span className='text-xs text-muted-foreground'>
                        Imported
                      </span>
                    )}
                    {removed ? (
                      <>
                        <span className='text-xs text-muted-foreground'>
                          Removed on save
                        </span>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          disabled={saving}
                          aria-label={`Undo removal of ${document.file_name}`}
                          onClick={() =>
                            setRemovedDocumentIds(current =>
                              current.filter(id => id !== document.id)
                            )
                          }
                        >
                          Undo
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          disabled={saving}
                          aria-label={`Open ${document.file_name}`}
                          onClick={() => handleViewDocument(document.id)}
                        >
                          <ExternalLink className='h-4 w-4' />
                        </Button>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          disabled={saving}
                          aria-label={`Remove ${document.file_name}`}
                          onClick={() =>
                            setRemovedDocumentIds(current => [
                              ...current,
                              document.id,
                            ])
                          }
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </>
                    )}
                  </li>
                );
              })}
              {pendingFiles.map((pending, index) => (
                <li key={index} className='flex items-center gap-2 text-sm'>
                  <Paperclip className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='truncate'>{pending.file.name}</span>
                  <span className='text-xs text-muted-foreground'>
                    {pending.documentId
                      ? 'Uploaded; attach on save'
                      : 'Upload on save'}
                  </span>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    disabled={saving}
                    aria-label={`Remove selected ${pending.file.name}`}
                    onClick={() =>
                      setPendingFiles(current =>
                        current.filter((_, i) => i !== index)
                      )
                    }
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={saving}
              className='mt-2'
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className='h-3.5 w-3.5 mr-1' />
              Add files
            </Button>
          </div>
        </div>

        <DialogFooter className='mt-2 shrink-0 gap-2'>
          {isEditing && (
            <Button
              variant='destructive'
              onClick={handleDelete}
              disabled={saving || deleting}
              className='mr-auto'
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={saving || deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || saving || deleting}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
